import type { PreparedSeries } from "../prepare-series";

type SeriesReading = { timeMs: number; value: number };

/** First index whose time is at or after timeMs. */
function lowerBound(points: PreparedSeries["points"], timeMs: number): number {
    let low = 0;
    let high = points.length;
    while (low < high) {
        const mid = Math.floor((low + high) / 2);
        if (points[mid][0].getTime() < timeMs) low = mid + 1;
        else high = mid;
    }
    return low;
}

/**
 * Nearest reading by time, the rule echarts' tooltip uses, but no further away than staleAfterMs so a
 * stream that went quiet reads as missing.
 */
export function findNearestReading(
    points: PreparedSeries["points"],
    timeMs: number,
    seriesType: PreparedSeries["seriesType"],
    staleAfterMs: number,
): SeriesReading | null {
    const at = lowerBound(points, timeMs);
    let beforeIndex = at - 1;
    let afterIndex = at;

    // A line's only nulls mark stale gaps. Step over them so the readings either side still count.
    if (seriesType === "line") {
        while (beforeIndex >= 0 && points[beforeIndex][1] === null) beforeIndex--;
        while (afterIndex < points.length && points[afterIndex][1] === null) afterIndex++;
    }

    const before = points[beforeIndex];
    const after = points[afterIndex];
    let nearest = before ?? after;
    if (before && after) {
        nearest = timeMs - before[0].getTime() <= after[0].getTime() - timeMs ? before : after;
    }

    // A null here is an empty bar bucket.
    if (!nearest || nearest[1] === null) return null;

    const nearestMs = nearest[0].getTime();
    if (Math.abs(timeMs - nearestMs) > staleAfterMs) return null;
    return { timeMs: nearestMs, value: nearest[1] };
}
